// Domains that block external image requests - skip these entirely
const BLOCKED_DOMAINS = [
  "instagram.com",
  "cdninstagram.com",
  "selectionrs.com",
  "metalearth.com",
  "merchandise.ford.com",
  "tirerack.com",
  "carsized.com",
];

/**
 * Check if a URL is from a domain known to block external requests
 */
export function isBlockedImageUrl(url: string): boolean {
  try {
    const hostname = new URL(url).hostname;
    return BLOCKED_DOMAINS.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch {
    return false;
  }
}

/**
 * Wraps an external image URL with our proxy endpoint to avoid CORS issues
 * @param url - The original image URL
 * @returns The proxied URL that goes through our Netlify function
 */
export function getProxiedImageUrl(url: string | undefined): string {
  if (!url) return "";

  // If it's already a proxied URL, return as-is
  if (url.includes("/.netlify/functions/proxy-image")) {
    return url;
  }

  // Encode the URL to safely pass it as a query parameter
  const encodedUrl = encodeURIComponent(url);
  return `/.netlify/functions/proxy-image?url=${encodedUrl}`;
}

/**
 * Sets up fallback to proxy for images that fail to load directly
 * Call this after images are added to the DOM
 *
 * Fallback chain:
 * 1. Direct load
 * 2. Proxy
 * 3. Hide the result if proxy also fails
 */
export function setupImageFallbacks(container: HTMLElement): void {
  const images = container.querySelectorAll("img");

  images.forEach((img) => {
    img.addEventListener("error", function handleImageError(e) {
      const target = e.target as HTMLImageElement;
      const currentSrc = target.src;

      // If this is not already proxied, try through proxy
      if (!currentSrc.includes("/.netlify/functions/proxy-image")) {
        console.warn(`Direct load failed for ${currentSrc}, trying proxy`);
        target.src = getProxiedImageUrl(currentSrc);
        return;
      }

      // Proxy failed - hide the entire result item
      console.warn(`Proxy failed, hiding image result`);
      const listItem = target.closest("li");
      if (listItem) {
        listItem.style.display = "none";
      } else {
        target.style.display = "none";
      }
    });
  });
}
